import { prisma, OrderStatus, OrderItemStatus, type Prisma } from "client";
import { getTicketWithCategory } from "../ticket";

export const getTicketQuantity = async (ticketId: string) => {
  try {
    const ticket = await getTicketWithCategory(ticketId);

    let quantity = ticket?.quantity;
    // if (ticket?.category?.shareQuantityAmongTickets) {
    //   quantity = ticket?.category.quantity;
    // }
    const reservedTicketCount = await getTicketCount(
      ticketId,
      OrderItemStatus.RESERVED
    );
    const completedOrder = await getTicketCount(
      ticketId,
      OrderItemStatus.CONFIRMED
    );
    const availableTicketCount = quantity
      ? quantity - reservedTicketCount - completedOrder
      : 0;

    return {
      reservedTicket: reservedTicketCount,
      availableTicket: availableTicketCount,
      completedOrder: completedOrder,
    };
  } catch (e) {
    console.error(e);
  }
};

export const getTicketCount = async (
  ticketId: string,
  orderItemStatus: OrderItemStatus
) => {
  const ticket = await prisma.ticket.findUnique({
    where: {
      id: ticketId,
    },
    include: {
      category: true,
    },
  });

  if (!ticket) {
    return 0;
  }

  if (ticket.category?.isSharedQuantity) {
    const ticketCategory = await prisma.ticketCategory.findUnique({
      where: {
        id: ticket.category.id,
      },
      include: {
        tickets: {
          select: {
            id: true,
          },
        },
      },
    });

    if (ticketCategory) {
      const ticketIds = ticketCategory.tickets.map((ticket) => ticket.id);
      const totalOrderCount = await prisma.orderItem.count({
        where: {
          ticketId: {
            in: ticketIds,
          },
          order: {
            status: {
              notIn: [
                OrderStatus.EXPIRED,
                OrderStatus.CANCELED,
                OrderStatus.DELETED,
              ],
            },
          },
          status: orderItemStatus,
        },
      });
      return totalOrderCount;
    }
  } else {
    const specificTicketOrderCount = await prisma.orderItem.count({
      where: {
        ticketId: ticketId,
        status: orderItemStatus,
      },
    });
    return specificTicketOrderCount;
  }

  return 0;
};

interface CountOrderItemsByTicketAndStatusPayload {
  ticketId: string;
  orderItemStatus: OrderItemStatus;
  isSharedQuantity: boolean;
  ticketIds: string[];
  excludeOrderId?: string;
}

export const countOrderItemsByTicketAndStatus = async (
  payload: CountOrderItemsByTicketAndStatusPayload,
  tx?: Prisma.TransactionClient
) => {
  const db = tx ?? prisma;

  const {
    orderItemStatus,
    isSharedQuantity,
    ticketId,
    ticketIds,
    excludeOrderId,
  } = payload;

  const orderFilter = {
    status: {
      notIn: [
        OrderStatus.EXPIRED,
        OrderStatus.CANCELED,
        OrderStatus.DELETED,
      ],
    },
    ...(excludeOrderId ? { id: { not: excludeOrderId } } : {}),
  };

  if (isSharedQuantity) {
    if (ticketIds.length === 0) {
      return 0;
    }

    return db.orderItem.count({
      where: {
        status: orderItemStatus,
        ticketId: {
          in: ticketIds,
        },
        order: orderFilter,
      },
    });
  }

  return db.orderItem.count({
    where: {
      status: orderItemStatus,
      ticketId,
      order: orderFilter,
    },
  });
};

export const resolveTicketInventoryContext = async (
  ticketId: string,
  tx?: Prisma.TransactionClient
) => {
  const db = tx ?? prisma;

  const ticket = await db.ticket.findUnique({
    where: { id: ticketId },
    select: {
      bookableItem: { select: { quantity: true } },
      category: {
        select: {
          quantity: true,
          isSharedQuantity: true,
          tickets: { select: { id: true } },
        },
      },
    },
  });

  if (!ticket) {
    throw new Error("Ticket not found");
  }

  const isSharedQuantity = ticket.category?.isSharedQuantity ?? false;
  const ticketIds = ticket.category?.tickets.map((t) => t.id) ?? [ticketId];
  const capacity = isSharedQuantity
    ? (ticket.category?.quantity ?? 0)
    : (ticket.bookableItem?.quantity ?? 0);

  return {
    capacity,
    isSharedQuantity,
    ticketIds,
  };
};

export type GetTicketAvailabilityOptions = {
  excludeOrderId?: string;
};

export const getTicketAvailability = async (
  ticketId: string,
  tx?: Prisma.TransactionClient,
  options?: GetTicketAvailabilityOptions
) => {
  try {
    const db = tx ?? prisma;
    const { capacity, isSharedQuantity, ticketIds } =
      await resolveTicketInventoryContext(ticketId, db);

    const countPayload = {
      ticketId,
      isSharedQuantity,
      ticketIds,
      excludeOrderId: options?.excludeOrderId,
    };

    const reservedTicketsCount = await countOrderItemsByTicketAndStatus(
      {
        ...countPayload,
        orderItemStatus: OrderItemStatus.RESERVED,
      },
      db
    );

    const confirmedTicketsCount = await countOrderItemsByTicketAndStatus(
      {
        ...countPayload,
        orderItemStatus: OrderItemStatus.CONFIRMED,
      },
      db
    );

    const availableTicketsCount = capacity
      ? capacity - reservedTicketsCount - confirmedTicketsCount
      : 0;

    return {
      capacity,
      reservedTickets: reservedTicketsCount,
      availableTickets: availableTicketsCount,
      confirmedTickets: confirmedTicketsCount,
      isSharedQuantity,
      ticketIds,
    };
  } catch (error) {
    console.error(error);
    throw new Error("Failed to get ticket availability");
  }
};

/**
 * Checks remaining ticket capacity at approval time.
 * Counts RESERVED + APPROVED + CONFIRMED items (pending-approval items are NOT counted).
 * Used by updateOrderItem to block organizers from over-approving.
 */
export const getTicketAvailabilityForApproval = async (
  ticketId: string,
  tx?: Prisma.TransactionClient
) => {
  const db = tx ?? prisma;

  const ticket = await db.ticket.findUnique({
    where: { id: ticketId },
    select: {
      bookableItem: { select: { quantity: true } },
      category: {
        select: {
          quantity: true,
          isSharedQuantity: true,
          tickets: { select: { id: true } },
        },
      },
    },
  });

  if (!ticket) {
    throw new Error("Ticket not found");
  }

  const isSharedQuantity = ticket.category?.isSharedQuantity ?? false;
  const ticketIds = ticket.category?.tickets.map((t) => t.id) ?? [ticketId];
  const capacity = isSharedQuantity
    ? (ticket.category?.quantity ?? 0)
    : (ticket.bookableItem?.quantity ?? 0);

  if (!capacity) {
    return { capacity: 0, taken: 0, available: 0 };
  }

  const countPayload = {
    ticketId,
    isSharedQuantity,
    ticketIds,
  };

  const [reserved, approved, confirmed] = await Promise.all([
    countOrderItemsByTicketAndStatus(
      { ...countPayload, orderItemStatus: OrderItemStatus.RESERVED },
      db
    ),
    countOrderItemsByTicketAndStatus(
      { ...countPayload, orderItemStatus: OrderItemStatus.APPROVED },
      db
    ),
    countOrderItemsByTicketAndStatus(
      { ...countPayload, orderItemStatus: OrderItemStatus.CONFIRMED },
      db
    ),
  ]);

  const taken = reserved + approved + confirmed;
  const available = capacity > 0 ? capacity - taken : 0;

  return { capacity, taken, available };
};
